import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ImagePlus, MessageCircleMore, SendHorizontal, Smile, X } from 'lucide-react';
import {
  fetchMySupportConversation,
  sendMySupportMessage,
  uploadSupportImages,
} from '../../utils/supportChat.js';
import '../../component-styles/SupportChatWidget.css';

function formatTimestamp(value) {
  if (!value) return '';

  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '';
  }
}

function formatTime(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '';
  }
}

export default function SupportChatWidget() {
  const EMOJI_OPTIONS = ['🙂', '😊', '😂', '😍', '😎', '🤔'];
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [conversation, setConversation] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const emojiRef = useRef(null);

  async function loadConversation({ silent = false } = {}) {
    if (!silent) setLoading(true);

    try {
      const data = await fetchMySupportConversation();
      setConversation(data.conversation || null);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load support chat');
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    loadConversation();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadConversation({ silent: true });
    }, open ? 10000 : 20000);

    return () => window.clearInterval(interval);
  }, [open]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conversation?.messages?.length, open]);

  useEffect(() => {
    if (!emojiOpen) return undefined;
    function handlePointerDown(event) {
      const clickedTrigger = emojiRef.current?.contains(event.target);
      if (!clickedTrigger) {
        setEmojiOpen(false);
      }
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') setEmojiOpen(false);
    }

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [emojiOpen]);

  const unreadCount = Number(conversation?.unreadForUser) || 0;
  const messages = useMemo(() => conversation?.messages || [], [conversation?.messages]);

  async function handleFilesSelected(event) {
    const files = event.target.files;
    if (!files?.length) return;

    try {
      setUploading(true);
      const uploaded = await uploadSupportImages(files);
      setAttachments((prev) => [...prev, ...uploaded]);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  async function handleDrop(event) {
    event.preventDefault();
    setDragActive(false);
    const files = event.dataTransfer?.files;
    if (!files?.length) return;

    try {
      setUploading(true);
      const uploaded = await uploadSupportImages(files);
      setAttachments((prev) => [...prev, ...uploaded]);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  }

  function removeAttachment(attachmentId) {
    setAttachments((prev) => prev.filter((item) => item.id !== attachmentId));
  }

  function handleComposerKeyDown(event) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    handleSubmit(event);
  }

  function handleEmojiSelect(emoji) {
    const textarea = textareaRef.current;
    if (!textarea) {
      setDraft((prev) => `${prev}${emoji}`);
      return;
    }

    const start = textarea.selectionStart ?? draft.length;
    const end = textarea.selectionEnd ?? draft.length;
    const next = `${draft.slice(0, start)}${emoji}${draft.slice(end)}`;
    setDraft(next);
    setEmojiOpen(false);

    window.requestAnimationFrame(() => {
      textarea.focus();
      const position = start + emoji.length;
      textarea.setSelectionRange(position, position);
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (sending || uploading) return;

    const body = draft.trim();
    if (!body && attachments.length === 0) return;

    try {
      setSending(true);
      const data = await sendMySupportMessage({ body, attachments });
      setConversation(data.conversation || null);
      setDraft('');
      setAttachments([]);
      setError('');
      setOpen(true);
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="support-chat-widget">
      <AnimatePresence>
        {open && (
          <motion.section
            className="support-chat-panel"
            aria-label="Support chat"
            initial={{ opacity: 0, y: 22, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
          <header className="support-chat-header">
            <div className="support-chat-header-main">
              <div className="support-chat-avatar-wrap">
                <div className="support-chat-avatar">IF</div>
                <span className="support-chat-online-dot" />
              </div>
              <div>
                <strong>Live Support</strong>
                <p>We typically respond within a few hours</p>
              </div>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close chat">
              <X size={16} />
            </button>
          </header>

          <div className="support-chat-body" ref={scrollRef}>
            {!loading ? <p className="support-chat-day">Today</p> : null}
            {loading ? (
              <p className="support-chat-empty">Loading conversation...</p>
            ) : messages.length === 0 ? (
              <p className="support-chat-empty">
                Send us a message and we'll get back to you as soon as possible.
              </p>
            ) : (
              messages.map((message) => {
                const isOwn = message.authorType !== 'admin';
                return (
                  <article
                    key={message.messageId}
                    className={`support-chat-message ${isOwn ? 'own' : 'reply'}`}
                  >
                    {!isOwn ? <div className="support-chat-inline-avatar">IF</div> : null}
                    <div className="support-chat-bubble">
                      {message.body ? <p>{message.body}</p> : null}
                      {Array.isArray(message.attachments) && message.attachments.length > 0 ? (
                        <div className="support-chat-images">
                          {message.attachments.map((attachment) => (
                            <a
                              key={attachment.id || attachment.url}
                              href={attachment.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img src={attachment.url} alt={attachment.name || 'Support upload'} />
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="support-chat-meta">
                      <time>{formatTime(message.createdAt) || formatTimestamp(message.createdAt)}</time>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          <form
            className="support-chat-composer"
            onSubmit={handleSubmit}
            onDragOver={(event) => {
              event.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setDragActive(false);
            }}
            onDrop={handleDrop}
            style={dragActive ? { outline: '2px dashed #7ca65c', outlineOffset: 4 } : undefined}
          >
            {attachments.length > 0 ? (
              <div className="support-chat-pending">
                {attachments.map((attachment) => (
                  <button
                    type="button"
                    key={attachment.id}
                    className="support-chat-chip"
                    onClick={() => removeAttachment(attachment.id)}
                  >
                    {attachment.name}
                    <X size={12} />
                  </button>
                ))}
              </div>
            ) : null}

            <textarea
              ref={textareaRef}
              rows={3}
              placeholder="Type your question..."
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleComposerKeyDown}
            />

            {error ? <p className="support-chat-status error">{error}</p> : null}

            <div className="support-chat-actions">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleFilesSelected}
              />
              <button
                type="button"
                className="ghost"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || sending}
              >
                <ImagePlus size={15} />
                {uploading ? 'Uploading...' : 'Add image'}
              </button>
              <div className="support-chat-emoji" ref={emojiRef}>
                <button
                  type="button"
                  className="ghost icon-only"
                  onClick={() => setEmojiOpen((value) => !value)}
                  aria-label="Pick emoji"
                  aria-expanded={emojiOpen}
                  disabled={uploading || sending}
                >
                  <Smile size={18} />
                </button>
                <AnimatePresence>
                  {emojiOpen ? (
                    <motion.div
                      className="support-chat-emoji-rail"
                      role="dialog"
                      aria-label="Emoji picker"
                      initial={{ opacity: 0, width: 0, x: -8, rotate: -2 }}
                      animate={{ opacity: 1, width: 'auto', x: 0, rotate: 0 }}
                      exit={{ opacity: 0, width: 0, x: -8, rotate: -2 }}
                      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {EMOJI_OPTIONS.map((emoji) => (
                        <button
                          type="button"
                          key={emoji}
                          className="support-chat-emoji-option"
                          onClick={() => handleEmojiSelect(emoji)}
                        >
                          {emoji}
                        </button>
                      ))}
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
              <button type="submit" disabled={sending || uploading}>
                <SendHorizontal size={16} />
              </button>
            </div>
          </form>
          </motion.section>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        className={`support-chat-launcher ${open ? 'open' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-label="Open support chat"
        whileHover={{ y: -2, scale: 1.02 }}
        whileTap={{ scale: 0.96 }}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      >
        <MessageCircleMore size={20} />
        <span>Chat</span>
        {unreadCount > 0 ? (
          <motion.span
            className="support-chat-badge"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.18 }}
          >
            {unreadCount}
          </motion.span>
        ) : null}
      </motion.button>
    </div>
  );
}
